import { FormEvent } from "react"
import { useForm, usePage, router } from "@inertiajs/react"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { Button } from "@/components/ui/button"
import { RichTextField } from "@/components/ui/rich-text-field"
import type { PageProps } from "@/types/inertia"

export type PostFormData = {
  id?: number
  title: string
  slug: string
  body: string
  excerpt: string
  cover_image_url: string
  author: string
  status: string
  published_at: string | null
  seo_title: string
  meta_description: string
  tag_list: string
}

export function PostForm({ initial }: { initial: PostFormData }) {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}
  const isEdit = Boolean(initial.id)

  const form = useForm({
    title: initial.title ?? "",
    slug: initial.slug ?? "",
    body: initial.body ?? "",
    excerpt: initial.excerpt ?? "",
    cover_image_url: initial.cover_image_url ?? "",
    author: initial.author ?? "",
    status: initial.status ?? "draft",
    published_at: initial.published_at ?? "",
    seo_title: initial.seo_title ?? "",
    meta_description: initial.meta_description ?? "",
    tag_list: initial.tag_list ?? "",
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (isEdit) {
      form.put(`/admin/posts/${initial.id}`)
    } else {
      form.post("/admin/posts")
    }
  }

  const remove = () => {
    if (initial.id && window.confirm("Delete this post? This cannot be undone.")) {
      router.delete(`/admin/posts/${initial.id}`)
    }
  }

  return (
    <form onSubmit={submit} className="mt-6 space-y-6">
      <div className="space-y-2">
        <label htmlFor="title">Title</label>
        <Input id="title" value={form.data.title} onChange={(e) => form.setData("title", e.target.value)} />
        {errors.title && <p className="text-xs text-danger-display">{errors.title}</p>}
      </div>

      <div className="space-y-2">
        <label htmlFor="slug">Slug</label>
        <Input
          id="slug"
          placeholder="auto-generated from title if blank"
          value={form.data.slug}
          onChange={(e) => form.setData("slug", e.target.value)}
        />
        {errors.slug && <p className="text-xs text-danger-display">{errors.slug}</p>}
      </div>

      <div className="space-y-2">
        <label htmlFor="excerpt">Excerpt</label>
        <Input
          id="excerpt"
          placeholder="Short summary for listings and search"
          value={form.data.excerpt}
          onChange={(e) => form.setData("excerpt", e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="cover_image_url">Cover image URL</label>
        <Input
          id="cover_image_url"
          placeholder="https://…"
          value={form.data.cover_image_url}
          onChange={(e) => form.setData("cover_image_url", e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <label>Body</label>
        {/* defaultValue is the initial body (stable); edits flow through onChange. */}
        <RichTextField
          defaultValue={initial.body ?? ""}
          onChange={(markdown) => form.setData("body", markdown)}
          placeholder="Write your post…"
        />
        <p className="text-xs text-ink-muted">
          Type <code>/</code> for headings, lists, quotes, and code blocks — or use Markdown
          (<code>#</code> heading, <code>-</code> list, <code>&gt;</code> quote). Select text for
          bold, italic, and links.
        </p>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <label htmlFor="status">Status</label>
          <Select id="status" value={form.data.status} onChange={(e) => form.setData("status", e.target.value)}>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </Select>
        </div>
        <div className="space-y-2">
          <label htmlFor="published_at">Publish date</label>
          <Input
            id="published_at"
            type="datetime-local"
            value={form.data.published_at ?? ""}
            onChange={(e) => form.setData("published_at", e.target.value)}
          />
        </div>
      </div>

      <div className="space-y-2">
        <label htmlFor="author">Author</label>
        <Input id="author" value={form.data.author} onChange={(e) => form.setData("author", e.target.value)} />
      </div>

      <div className="space-y-2">
        <label htmlFor="tag_list">Tags</label>
        <Input
          id="tag_list"
          placeholder="podcasting, strategy, equipment"
          value={form.data.tag_list}
          onChange={(e) => form.setData("tag_list", e.target.value)}
        />
        <p className="text-xs text-ink-muted">
          Comma-separated. Tags group posts into topic clusters and power the
          "Keep reading" related-posts section.
        </p>
      </div>

      <details className="rounded-md border border-hairline bg-surface p-4">
        <summary className="cursor-pointer text-sm font-medium text-ink-display">SEO overrides (optional)</summary>
        <div className="mt-4 space-y-4">
          <div className="space-y-2">
            <label htmlFor="seo_title">SEO title</label>
            <Input id="seo_title" value={form.data.seo_title} onChange={(e) => form.setData("seo_title", e.target.value)} />
          </div>
          <div className="space-y-2">
            <label htmlFor="meta_description">Meta description</label>
            <Input id="meta_description" value={form.data.meta_description} onChange={(e) => form.setData("meta_description", e.target.value)} />
          </div>
        </div>
      </details>

      <div className="flex items-center gap-3 border-t border-hairline pt-6">
        <Button type="submit" disabled={form.processing}>
          {form.processing ? "Saving…" : isEdit ? "Save post" : "Create post"}
        </Button>
        {isEdit && (
          <Button type="button" variant="danger" onClick={remove}>
            Delete
          </Button>
        )}
      </div>
    </form>
  )
}
